import { Box, Dialog, DialogContent, Divider } from '@mui/material';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { CustomDialogTitle } from '../../../../Common/Custom/CustomDialogTitle';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { Deviations } from '../../../../../Component/Model/Dto/EmploymentEquity/Deviations';
import { DeviationsService } from '../../../../../Component/Api/EmploymentEquity/Deviations/DeviationsService';
import { CoreStaticDataService } from '../../../../../Component/Api/StaticData/CoreStaticDataService';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import AppSelectList, { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppSelectListWithGroups, { IDropDownOptionsGroup } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectListWithGroups';
import AppMultiSelect from '../../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import { AppQuill } from '../../../../../Component/Common/Components/FormValidationInputs/AppQuill';
import { PortalDocumentsDropZone } from '../../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { ConfirmationDialog } from '../../../../../Component/Common/Components/ConfirmationDialog';
import { ModalDialogActions } from '../../../../../Component/Common/Components/Modal/ModalDialogActions';
import { AuditDateDetails } from '../../../../../Component/Common/Components/Modal/AuditDateDetails';
import { demographicDeviationSchema } from './DemographicDeviationModalValidation';
import { EEOccupationalLevel } from '../../../../../Component/Model/Enum/EmploymentEquity';
import { occupationalLevelDisplayNames } from '../../Shared/deviationsCommon';
import { DemographicGroupListWithForeignNationals } from '../../../../../Component/Model/Enum/MultiSelectOptions/DemographicGroupOptions';

const demographicGroupOptions: IDropDownOptions[] = DemographicGroupListWithForeignNationals.map(item => ({ id: item.value, value: item.label }));

const toDemographicGroupId = (raceId: number | null | undefined, genderId: number | null | undefined): string | null => {
    if (!raceId || !genderId) return null;
    return `${raceId}_${genderId}`;
};

const fromDemographicGroupId = (id: string | null | undefined): { raceId: number | null; genderId: number | null } => {
    if (!id) return { raceId: null, genderId: null };
    const parts = id.split('_');
    return { raceId: Number(parts[0]), genderId: Number(parts[1]) };
};

const toFormValues = (dto: Deviations.DtoOut) => {
    const demographicGroup = toDemographicGroupId(dto.raceId, dto.genderId);
    const plannedDemographicGroups = (dto.plannedDemographics ?? [])
        .map(d => toDemographicGroupId(d.raceId, d.genderId))
        .filter((id): id is string => id !== null);
    const actualDemographicGroups = (dto.actualDemographics ?? [])
        .map(d => toDemographicGroupId(d.raceId, d.genderId))
        .filter((id): id is string => id !== null);
    return { ...dto, demographicGroup, plannedDemographicGroups, actualDemographicGroups };
};

interface IProps {
    id?: number;
    occupationalLevelType: number;
    scorecardId: string;
    showModal: boolean;
    handleCancel: () => void;
    defaultRaceId?: number;
    defaultGenderId?: number;
    year?: number | null;
    isDisabled?: boolean;
}

export const DemographicDeviationModal = (props: IProps) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
    const [deviationId, setDeviationId] = React.useState<number | undefined>(props.id);
    const [goalAndTargetRowYearId, setGoalAndTargetRowYearId] = React.useState<number | null>(props.year ?? null);
    const [reasonGroups, setReasonGroups] = React.useState<IDropDownOptionsGroup[]>([]);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = React.useState<boolean>(false);
    const [loadedDto, setLoadedDto] = React.useState<Deviations.DtoOut | null>(null);

    const commentMaxLength = 6000;
    const [remainingCommentCharacters, setRemainingCommentCharacters] = React.useState(commentMaxLength);
    const remedialPlanMaxLength = 1000;
    const [remainingRemedialPlanCharacters, setRemainingRemedialPlanCharacters] = React.useState(remedialPlanMaxLength);

    const { handleSubmit, control, reset, watch, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(demographicDeviationSchema),
    });

    const watchedReasonId = watch('reasonId');
    const showEnhancedFields = [1, 2, 3].includes(Number(watchedReasonId));

    const fetchReasonGroups = async () => {
        try {
            const result = await CoreStaticDataService.getGroupedDeviationReasons();
            setReasonGroups(result.data.map(g => ({
                group: g.group,
                items: g.items.map(item => ({ id: item.id, value: item.name })),
            })));
        } catch (error) {
            console.log(error);
        }
    };

    const getById = async (id: number) => {
        try {
            const result = await DeviationsService.GetById(props.scorecardId, id);
            setGoalAndTargetRowYearId(result.data.goalAndTargetRowYearId);
            setLoadedDto(result.data);
            reset(toFormValues(result.data));
        } catch (error) {
            console.log(error);
        }
    };

    const handleFormError = () => {
        const form = document.getElementById('demographicDeviationsForm');
        if (!form) return;
        const firstInvalid = form.querySelector<HTMLElement>('[aria-invalid="true"]');
        if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            const dialogContent = form.closest<HTMLElement>('.MuiDialogContent-root');
            if (dialogContent) dialogContent.scrollTop = 0;
        }
    };

    const deleteDeviation = async () => {
        setIsDeleting(true);
        try {
            if (deviationId) {
                await DeviationsService.Delete(props.scorecardId, deviationId);
                toast.success('Deviation deleted successfully');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsDeleting(false);
            handleCloseConfirmation();
            props.handleCancel();
        }
    };

    const handleDataSubmit = async (data: any) => {
        setIsLoading(true);
        try {
            const { raceId, genderId } = fromDemographicGroupId(data.demographicGroup);

            const plannedDemographics: Deviations.AppointmentDemographicDtoIn[] = showEnhancedFields
                ? ((data.plannedDemographicGroups as string[] | null) ?? []).flatMap(id => {
                    const parsed = fromDemographicGroupId(id);
                    if (parsed.raceId !== null && parsed.genderId !== null) {
                        return [{ raceId: parsed.raceId, genderId: parsed.genderId }];
                    }
                    return [];
                })
                : [];

            const actualDemographics: Deviations.AppointmentDemographicDtoIn[] = showEnhancedFields
                ? ((data.actualDemographicGroups as string[] | null) ?? []).flatMap(id => {
                    const parsed = fromDemographicGroupId(id);
                    if (parsed.raceId !== null && parsed.genderId !== null) {
                        return [{ raceId: parsed.raceId, genderId: parsed.genderId }];
                    }
                    return [];
                })
                : [];

            const dtoIn: Deviations.DtoIn = {
                scorecardId: props.scorecardId,
                eEOccupationLevelId: props.occupationalLevelType,
                raceId,
                genderId,
                isDisabled: props.isDisabled ?? false,
                goalAndTargetRowYearId: goalAndTargetRowYearId,
                reasonId: data.reasonId,
                comment: data.comment,
                eEA2KeyJustifiableReason: null,
                remedialPlan: data.remedialPlan,
                plannedDemographics,
                actualDemographics,
            };
            if (deviationId === undefined) {
                const result = await DeviationsService.Create(dtoIn);
                setDeviationId(result.data.id);
                setLoadedDto(result.data);
                reset(toFormValues(result.data));
                toast.success('Deviation created successfully');
                setTimeout(() => {
                    const form = document.getElementById('demographicDeviationsForm');
                    const dialogContent = form?.closest<HTMLElement>('.MuiDialogContent-root');
                    if (dialogContent) {
                        dialogContent.scrollTop = dialogContent.scrollHeight;
                    }
                }, 300);
            } else {
                dtoIn.id = deviationId;
                const result = await DeviationsService.Update(dtoIn);
                setLoadedDto(result.data);
                reset(toFormValues(result.data));
                toast.success('Deviation updated successfully');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = () => {
        setShowDeleteConfirmationDialog(true);
    };

    const handleCloseConfirmation = () => {
        setShowDeleteConfirmationDialog(false);
    };

    useEffect(() => {
        setIsLoading(true);
        fetchReasonGroups();
        if (deviationId !== undefined) {
            getById(deviationId);
        } else {
            setLoadedDto(null);
            const defaultGroup = toDemographicGroupId(props.defaultRaceId, props.defaultGenderId);
            if (defaultGroup) {
                setValue('demographicGroup', defaultGroup);
            }
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviationId]);

    return (
        <>
            <Dialog disableEnforceFocus open={props.showModal} onClose={props.handleCancel} aria-labelledby='demographicDialogTitle' maxWidth='md' fullWidth scroll='paper'>
                <CustomDialogTitle id='demographicDialogTitle' onClose={props.handleCancel}>
                    {deviationId === undefined ? 'Add' : 'Edit'} Demographic Level Justification - {occupationalLevelDisplayNames[props.occupationalLevelType as EEOccupationalLevel]}
                </CustomDialogTitle>
                <DialogContent>
                    {deviationId === undefined && (
                        <MDTypography variant='caption' sx={{ display: 'block', mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                            You will be able to upload supporting documents after completing the form.
                        </MDTypography>
                    )}
                    {isLoading ? (
                        <SmallLoading />
                    ) : (
                        <Box component='form' id='demographicDeviationsForm' onSubmit={handleSubmit(handleDataSubmit, handleFormError)} sx={{ mt: 1 }}>
                            <MDBox mb={2}>
                                <AppSelectList
                                    control={control}
                                    name='demographicGroup'
                                    label='Demographic Group*'
                                    items={demographicGroupOptions}
                                    size='small'
                                />
                            </MDBox>
                            <MDBox mb={2}>
                                <AppSelectListWithGroups
                                    control={control}
                                    name='reasonId'
                                    label='Justification Reason*'
                                    groups={reasonGroups}
                                    size='small'
                                />
                            </MDBox>
                            <MDBox mb={2}>
                                <AppQuill control={control} name='comment' label='Commentary*' maxLength={commentMaxLength} />
                            </MDBox>
                            {showEnhancedFields && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <MDTypography variant='h6' sx={{ mt: 1, mb: 2 }}>
                                        Employment Equity Plan: Planned vs Actual
                                    </MDTypography>
                                    <MDBox mb={2}>
                                        <AppMultiSelect
                                            control={control}
                                            name='plannedDemographicGroups'
                                            label='Planned Appointments'
                                            options={demographicGroupOptions}
                                            size='small'
                                        />
                                    </MDBox>
                                    <MDBox mb={2}>
                                        <AppMultiSelect
                                            control={control}
                                            name='actualDemographicGroups'
                                            label='Actual Appointments'
                                            options={demographicGroupOptions}
                                            size='small'
                                        />
                                    </MDBox>
                                </>
                            )}
                            <MDBox mb={2}>
                                <AppQuill control={control} name='remedialPlan' label='Remedial Plan' maxLength={remedialPlanMaxLength} />
                            </MDBox>
                            {deviationId !== undefined && (
                                <MDBox mt={2} mb={2}>
                                    <PortalDocumentsDropZone id={deviationId} link={'deviation'} />
                                </MDBox>
                            )}
                            {loadedDto && (
                                <AuditDateDetails
                                    heading='Justification Date Details'
                                    createdDateUTC={loadedDto.createdDate}
                                    createdByName={loadedDto.createdBy}
                                    lastModifiedDateUTC={loadedDto.lastModifiedDate}
                                    lastModifiedByName={loadedDto.lastModifiedBy}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <ModalDialogActions
                    formId='demographicDeviationsForm'
                    isSubmitting={isLoading}
                    onCancel={props.handleCancel}
                    showDelete={deviationId !== undefined}
                    onDelete={handleDelete}
                />
            </Dialog>
            <ConfirmationDialog
                showDialog={showDeleteConfirmationDialog}
                handleAccept={deleteDeviation}
                handleCancel={handleCloseConfirmation}
                heading={'Delete Justification'}
                message={'Are you sure you want to delete this justification and associated files?'}
                isLoading={isDeleting}
            />
        </>
    );
};
